// 取り下げ (自動取消) 時刻の入力検証。
//
// 取り下げ時刻 = 約定しなかった札を GC までに自動取消する予約時刻 (spec AC-BIN-004 の
// 「自動取消時刻」)。上限は GC ちょうど: GC より後の時刻は、GC 到達時点で板の札が終わる
// (spec: GC = 受渡コマ開始の 1 時間前、GC 済みコマの入札作成・編集は不可) ため予約しても
// 発火しない。GC ちょうどは「GC と同時」 の自動取消として受け付ける (AC-BIN-017。デフォルト
// 取り下げ時刻「直前」 の初期値)。下限は現在時刻: 過ぎた時刻を予約しても取り下げは発火しない。
// どちらも入力段階で弾かないと「取り下げたつもりの札が GC まで板に残る」誤発注につながる。
import { DefaultWithdrawOffset } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'

import {
  formatJstDateTimeLocal,
  parseJstDateTimeLocal,
} from '~/models/dateTime'
import { gcStatusOfKoma } from '~/pages/IntradayBidding/models/deliveryDate'
import { calcGcAtForKoma } from '~/pages/IntradayBidding/models/gcCountdown'

/**
 * 取り下げ時刻の違反 (= 表示する message の出し分けに使う)。 `afterGc` は文言に出す
 * 対象コマの GC を持つ (判定に使った GC をそのまま返し、 呼び出し側で再計算させない)。
 */
export type WithdrawAtViolation =
  | { kind: 'afterGc'; gcAt: Date }
  | { kind: 'past' }

const isKoma = (koma: number): boolean =>
  Number.isInteger(koma) && koma >= 1 && koma <= 48

/**
 * 取り下げ時刻 (datetime-local の生入力) が「現在時刻より後、対象コマの GC まで」かを
 * 検証し、違反していればその種別を返す (違反なしは `undefined`)。
 *
 * 上限は GC ちょうどを含む (GC と同時の自動取消、AC-BIN-017)。下限は現在時刻ちょうどを
 * 含まない (予約が即座に無意味になる)。
 * 検証対象外のケース (未入力 / 形式不正) は `undefined` を返す。コマ未選択・値域外は
 * GC を算出できないため GC 判定のみ飛ばす (= 同じ入力ミスで複数 message を並べない。
 * コマに依存しない下限判定はそのまま行う)。
 *
 * 両方に違反する場合 (= GC を過ぎたコマに、 GC より後かつ過去の時刻を指定) は `afterGc` を
 * 返す。 GC が業務上の hard 制約で、 取り下げ時刻を直しても解消しないため。
 *
 * 対象コマが既に GC を過ぎている (= 札作成自体ができない) 時、 GC まで (GC ちょうどを含む) の
 * 取り下げ時刻は違反にしない。 「現在時刻より後に」 と案内しても直せないので、 送信時の GC 超過の通知
 * (`useIntradayBidCreateSubmit` の `gcExceeded`) に委ねる。
 *
 * @param withdrawAtLocal `<input type="datetime-local">` の値 ("YYYY-MM-DDTHH:mm"、未入力は空文字)
 * @param koma 対象コマ番号 (1〜48。未選択は `NaN`)
 * @param deliveryDate 受渡日 (時刻部分は参照しない)
 * @param now 判定時点の現在時刻
 */
export const validateWithdrawAt = ({
  withdrawAtLocal,
  koma,
  deliveryDate,
  now,
}: {
  withdrawAtLocal: string
  koma: number
  deliveryDate: Date
  now: Date
}): WithdrawAtViolation | undefined => {
  // 任意項目のため未入力は検証しない。
  if (withdrawAtLocal === '') return undefined

  // datetime-local の入力欄に出るのは業務上の JST 壁時計 (= 同じ画面の GC・コマ帯と
  // 同じ軸) なので、 端末 TZ ではなく JST として解釈する (#4765)。 GC も JST 壁時計で
  // 組まれる (calcGcAtForKoma) ため、 両者を同一時間軸で比較できる。
  const withdrawAt = parseJstDateTimeLocal(withdrawAtLocal)
  // 形式不正 (datetime-local からは発生しないが、 prefill 等で壊れた値が入った場合)
  // は判定対象外とする (= 形式の error を新設せず既存の submit 経路に委ねる)。
  if (withdrawAt === undefined) return undefined

  // コマが確定していないと GC を算出できない (= コマ側の error が先に出る)。
  const gcAt = isKoma(koma) ? calcGcAtForKoma(deliveryDate, koma) : undefined
  if (gcAt !== undefined && withdrawAt.getTime() > gcAt.getTime()) {
    return { kind: 'afterGc', gcAt }
  }

  // GC 済みコマは取り下げ時刻の問題でない。 送信時の GC 超過の判定と同じ関数で決める。
  if (
    gcAt !== undefined &&
    gcStatusOfKoma(now, deliveryDate, koma) === 'closed'
  ) {
    return undefined
  }

  if (withdrawAt.getTime() <= now.getTime()) return { kind: 'past' }

  return undefined
}

// 直前 (0 分) は GC と同時に自動取り下げする設定で、 初期値は GC ちょうど (AC-BIN-017)。
const DEFAULT_WITHDRAW_OFFSET_MINUTES: Record<
  DefaultWithdrawOffset,
  number | undefined
> = {
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_UNSPECIFIED]: undefined,
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_IMMEDIATE]: 0,
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_30_MIN]: 30,
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_1_HOUR]: 60,
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_1_HOUR_30_MIN]: 90,
  [DefaultWithdrawOffset.DEFAULT_WITHDRAW_OFFSET_2_HOUR]: 120,
}

/**
 * 札作成モーダルの自動取消時刻の初期値 (AC-BIN-004)。 デフォルト取り下げ時刻 (AC-BDL-501) の
 * 「対象コマの GC − N 分」 を datetime-local 値で返す (直前 = 0 分は GC ちょうど)。
 * 設定が決まらない時 (未設定・取得中・コマ未選択) は空文字 (= 自動取消を予約しない)。
 */
export const defaultWithdrawAtLocal = ({
  offset,
  koma,
  deliveryDate,
}: {
  offset: DefaultWithdrawOffset | undefined
  koma: number
  deliveryDate: Date
}): string => {
  const minutes =
    offset === undefined ? undefined : DEFAULT_WITHDRAW_OFFSET_MINUTES[offset]
  if (minutes === undefined || !isKoma(koma)) return ''
  const gcAt = calcGcAtForKoma(deliveryDate, koma)
  return formatJstDateTimeLocal(new Date(gcAt.getTime() - minutes * 60_000))
}

/**
 * 取り下げ時刻ピッカー (datetime-local) の選択可能範囲 `{ min, max }`。
 * 範囲は「現在時刻より後 〜 対象コマの GC (ちょうどを含む)」で、 [[validateWithdrawAt]] の
 * 判定 (past = 現在時刻以下 / afterGc = GC より後) と一致させる。
 *
 * - `min`: datetime-local は分単位で、 現在時刻ちょうど (以下) は past になるため、
 *   現在の分を切り捨てた**次の分**にする (10:00:30 → 10:01、 10:00:00 → 10:01)。
 * - `max`: GC ちょうど。 コマ 1・2 は GC が前日にまたがるので日付も前日になる。
 *
 * どちらも JST 壁時計 ([[formatJstDateTimeLocal]]。 [[defaultWithdrawAtLocal]] と同じ変換)。
 * コマ未選択・値域外は GC を算出できないため `max` のみ `undefined` (= 上限なし)。
 * GC 済みコマでは `min > max` になり選べる値が無い (= そのコマは札作成自体ができない)。
 */
export const withdrawAtPickerRange = ({
  koma,
  deliveryDate,
  now,
}: {
  koma: number
  deliveryDate: Date
  now: Date
}): { min: string; max: string | undefined } => {
  const nextMinuteMs = (Math.floor(now.getTime() / 60_000) + 1) * 60_000
  return {
    min: formatJstDateTimeLocal(new Date(nextMinuteMs)),
    max: isKoma(koma)
      ? formatJstDateTimeLocal(calcGcAtForKoma(deliveryDate, koma))
      : undefined,
  }
}
